import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import userStore from "../zustand/userStore";
import { BASE_URL } from "../configs/WebServiceUrl";

const ROWS_PER_PAGE = 10;

export default function MyInvoices() {
  const userData = userStore((state) => state.user);
  const schoolId = userData?.admin_id || userData?.id;

  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  useEffect(() => {
    if (!schoolId) return;

    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await axios.get(`${BASE_URL}/general/invoices/${schoolId}`);

        if (res.data?.success && Array.isArray(res.data.data)) {
          setInvoices(res.data.data);
        } else {
          setInvoices([]);
        }
      } catch (err) {
        console.error("❌ Failed to fetch invoices:", err);
        setError("Failed to load invoices. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [schoolId]);

  const totalPages = Math.ceil(invoices.length / ROWS_PER_PAGE);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return invoices.slice(start, start + ROWS_PER_PAGE);
  }, [invoices, page]);

  // Helper to build the invoice PDF URL using direct host
  const getInvoicePdfUrl = (pdfPath) => {
    if (!pdfPath) return "";
    if (pdfPath.startsWith("http://") || pdfPath.startsWith("https://")) {
      return pdfPath;
    }
    const cleanPath = pdfPath.startsWith("/") ? pdfPath : `/${pdfPath}`;
    return `https://web1024.docmaster.in:5004${cleanPath}`;
  };

  // Handler for PDF download
  const handleDownload = async (invoice) => {
    if (!invoice?.invoice_pdf_path) {
      alert("Invoice PDF is not available.");
      return;
    }

    try {
      setActionLoadingId(`download-${invoice.id}`);
      const pdfUrl = getInvoicePdfUrl(invoice.invoice_pdf_path);
      const safeFilename = `${(invoice.invoice_number || `invoice_${invoice.id}`).replace(/[\/\\:]/g, "_")}.pdf`;

      // Try fetching as blob to trigger local browser file save
      try {
        const response = await fetch(pdfUrl);
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = safeFilename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
          return;
        }
      } catch (corsErr) {
        console.warn("Direct blob fetch failed, falling back to direct link download:", corsErr);
      }

      // Fallback: standard link download
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = safeFilename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error initiating download:", err);
      alert("Failed to download invoice. Please try again.");
    } finally {
      setTimeout(() => setActionLoadingId(null), 1000);
    }
  };


  const formatCurrency = (val) => {
    const num = parseFloat(val || 0);
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ marginTop: "12px", color: "#64748b", fontSize: "14px" }}>Loading invoices...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" style={{ margin: "20px 0", fontSize: "13px" }}>
        {error}
      </div>
    );
  }

  if (!invoices.length) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <div style={{ fontSize: "40px", marginBottom: "10px" }}>🧾</div>
        <h6 style={{ fontWeight: "700", color: "#1e293b", marginBottom: "6px" }}>No Invoices Found</h6>
        <p style={{ color: "#64748b", fontSize: "13px" }}>
          Invoices generated for your subscription payments will appear here.
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "-8px" }}>
      {/* ===== Table ===== */}
      <div style={{ overflowX: "auto" }}>
        <table
          className="table table-sm table-bordered table-hover"
          style={{
            fontSize: "11px",
            lineHeight: "1.2",
            verticalAlign: "middle",
            marginTop: 0,
            marginBottom: "8px",
          }}
        >
          <thead>
            <tr style={{ height: "36px", borderBottom: "2px solid #cbd5e1" }}>
              <th style={{ padding: "8px 6px", textAlign: "center", width: "45px", background: "none", backgroundColor: "transparent" }}>S.No</th>
              <th style={{ padding: "8px 6px", background: "none", backgroundColor: "transparent" }}>Invoice No.</th>
              <th style={{ padding: "8px 6px", background: "none", backgroundColor: "transparent" }}>Date</th>
              <th style={{ padding: "8px 6px", background: "none", backgroundColor: "transparent" }}>Plan Name</th>
              <th style={{ padding: "8px 6px", textAlign: "right", background: "none", backgroundColor: "transparent" }}>Amount</th>
              <th style={{ padding: "8px 6px", textAlign: "center", background: "none", backgroundColor: "transparent" }}>Status</th>
              <th style={{ padding: "8px 6px", textAlign: "center", width: "95px", background: "none", backgroundColor: "transparent" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={item.id} style={{ height: "26px" }}>
                <td style={{ padding: "3px 6px", textAlign: "center", fontWeight: "600" }}>
                  {(page - 1) * ROWS_PER_PAGE + index + 1}
                </td>
                <td style={{ padding: "3px 6px", fontWeight: "600", color: "#0f172a" }}>
                  {item.invoice_number || "-"}
                </td>
                <td style={{ padding: "3px 6px", color: "#475569" }}>
                  {item.invoice_date
                    ? new Date(item.invoice_date).toLocaleDateString("en-GB").replace(/\//g, "-")
                    : "-"}
                </td>
                <td style={{ padding: "3px 6px", color: "#334155" }}>
                  {item.plan_name || "DocMaster Subscription"}
                </td>
                <td style={{ padding: "3px 6px", textAlign: "right", fontWeight: "700", color: "#047857" }}>
                  {formatCurrency(item.total_payable)}
                </td>
                <td style={{ padding: "3px 6px", textAlign: "center" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "1px 6px",
                      borderRadius: "10px",
                      fontSize: "10px",
                      fontWeight: "600",
                      textTransform: "capitalize",
                      backgroundColor:
                        item.payment_status === "success"
                          ? "#dcfce7"
                          : item.payment_status === "pending"
                          ? "#fef9c3"
                          : "#fee2e2",
                      color:
                        item.payment_status === "success"
                          ? "#15803d"
                          : item.payment_status === "pending"
                          ? "#854d0e"
                          : "#b91c1c",
                    }}
                  >
                    {item.payment_status || "Success"}
                  </span>
                </td>
                <td style={{ padding: "3px 6px", textAlign: "center" }}>
                  {/* Download Button */}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleDownload(item)}
                    disabled={!item.invoice_pdf_path || actionLoadingId === `download-${item.id}`}
                    style={{
                      padding: "1px 7px",
                      fontSize: "10px",
                      fontWeight: "600",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "3px",
                      borderRadius: "4px",
                      opacity: !item.invoice_pdf_path ? 0.5 : 1,
                    }}
                    title={!item.invoice_pdf_path ? "PDF not available" : "Download PDF"}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    {actionLoadingId === `download-${item.id}` ? "..." : "Download"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ===== Pagination ===== */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "6px",
            marginTop: "12px",
            alignItems: "center",
          }}
        >
          {/* Prev */}
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            style={{ borderRadius: "6px" }}
          >
            ◀
          </button>

          {/* Page Numbers */}
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`btn btn-sm ${
                p === page ? "btn-primary" : "btn-outline-secondary"
              }`}
              onClick={() => setPage(p)}
              style={{ minWidth: "30px", borderRadius: "6px" }}
            >
              {p}
            </button>
          ))}

          {/* Next */}
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            style={{ borderRadius: "6px" }}
          >
            ▶
          </button>
        </div>
      )}
    </div>
  );
}
